import { default as isPlainObject } from 'lodash/isPlainObject';
import { default as isString } from 'lodash/isString';
import { default as isUndefined } from 'lodash/isUndefined';

import { PartialRequired } from '@vimeo/core/types/generic';

import {
  TErrorMessages,
  validateBoolean,
  validateDateField,
  validateNumber,
  validateObject,
  validateString,
} from './validator_utils';
import { interactionToolsConfig } from '../../configs/InteractionToolsConfig';
import {
  EPollState,
  EQuestionState,
  IChatMessageAuthorRecord,
  IChatMessageRecord,
  IPoll,
  IPollOption,
  IQnAQuestionAuthor,
  IQnAQuestionRecord,
  IQnAQuestionReplyRecord,
  IQnASessionRecord,
} from '../../types/interaction_tools';

/**
 * Validates the poll interaction option object and returns messages with the errors if it's not valid
 */
export function collectPollOptionInteractionErrors({
  id,
  text,
  votesCount,
  correct,
}: PartialRequired<IPollOption, 'id' | 'text' | 'correct' | 'votesCount'>): TErrorMessages {
  const errors: TErrorMessages = [];

  validateString({ field: id, fieldName: 'id', errors });
  validateString({ field: text, fieldName: 'text', errors });
  validateBoolean({ field: correct, fieldName: 'correct', errors });

  if (votesCount !== undefined) {
    validateNumber({ field: votesCount, fieldName: 'votesCount', errors });
  }

  return errors;
}

/**
 * Validates the poll interaction object and returns messages with the errors if it's not valid
 */
export function collectPollInteractionErrors({
  id,
  createdAt,
  state,
  question,
  options,
}: PartialRequired<IPoll, 'id' | 'question' | 'options' | 'state' | 'createdAt'>): TErrorMessages {
  const errors: TErrorMessages = [];
  validateString({ field: id, fieldName: 'id', errors });
  validateString({ field: question, fieldName: 'question', errors });
  validateDateField({ field: createdAt, fieldName: 'createdAt', errors });

  if (!Object.values(EPollState).includes(state)) {
    errors.push(`state should exists in EPollState, ${state}`);
  }

  if (
    !Array.isArray(options) ||
    options.some((opt) => collectPollOptionInteractionErrors(opt).length > 0)
  ) {
    errors.push(
      `options should be an array and all options should be valid, but found  ${JSON.stringify(
        options,
      )}`,
    );
  }
  if (Array.isArray(options) && (options.length < 2 || options.length > 4)) {
    errors.push(`options length should be between 2 and 4, but found  ${options.length}`);
  }
  return errors;
}

/**
 * Validates the question session interaction object and returns messages with the errors if it's not valid
 */
export function collectQuestionSessionInteractionErrors({
  id,
  createdAt,
  questions,
  pendingQuestions,
  startedAt,
  isQnaModerated,
}: PartialRequired<
  IQnASessionRecord,
  'id' | 'createdAt' | 'isQnaModerated' | 'startedAt'
>): TErrorMessages {
  const errors: TErrorMessages = [];
  validateString({ field: id, fieldName: 'id', errors });
  validateDateField({ field: createdAt, fieldName: 'createdAt', errors });
  validateDateField({ field: startedAt, fieldName: 'startedAt', errors });
  validateBoolean({ field: isQnaModerated, fieldName: 'isQnaModerated', errors });

  if (questions !== undefined && !isPlainObject(questions)) {
    errors.push(`questions should be a plain object, but found  ${JSON.stringify(questions)}`);
  }

  if (pendingQuestions !== undefined && !isPlainObject(pendingQuestions)) {
    errors.push(
      `pendingQuestions should be a plain object or undefined, but found  ${JSON.stringify(
        questions,
      )}`,
    );
  }
  return errors;
}

/**
 * Validates the question interaction object and returns messages with the errors if it's not valid
 */
export function collectQuestionInteractionErrors({
  id,
  anonymous,
  createdAt,
  state,
  text,
  user,
  votes,
}: IQnAQuestionRecord) {
  const errors: TErrorMessages = [];

  validateString({ field: id, fieldName: 'id', errors });
  validateBoolean({ field: anonymous, fieldName: 'anonymous', errors });
  validateString({ field: text, fieldName: 'text', errors });

  if (
    isString(text) &&
    (text.length > interactionToolsConfig.QNA.MAX_QUESTION_LENGTH || !text.length)
  ) {
    errors.push(`Qna question did not pass validation: ${text}, length: ${text}`);
  }

  validateDateField({ field: createdAt, fieldName: 'createdAt', errors });
  validateObject({ field: votes, fieldName: 'votes', errors });

  if (!Object.values(EQuestionState).includes(state)) {
    errors.push(`Qna question state should exists in EPollState, ${state}`);
  }

  if (
    user !== null &&
    user !== undefined &&
    collectMessageAuthorInteractionErrors(user).length > 0
  ) {
    errors.push(`user should be optional plain object, but found  ${JSON.stringify(user)}`);
  }

  return errors;
}

/**
 * Validates the question reply interaction object and returns messages with the errors if it's not valid
 */
export function collectQuestionReplyInteractionErrors({
  questionId,
  createdAt,
  text,
  user,
  votes,
}: IQnAQuestionReplyRecord) {
  const errors: TErrorMessages = [];

  validateString({ field: questionId, fieldName: 'questionId', errors });
  validateString({ field: text, fieldName: 'text', errors });

  if (
    isString(text) &&
    (text.length > interactionToolsConfig.QNA.MAX_QUESTION_REPLY_LENGTH || !text.length)
  ) {
    errors.push(`Qna question reply did not pass validation: ${text}, length: ${text}`);
  }

  validateDateField({ field: createdAt, fieldName: 'createdAt', errors });
  validateObject({ field: votes, fieldName: 'votes', errors });

  if (user === undefined) {
    errors.push(`user should be plain object, but found undefined`);
  } else {
    collectMessageAuthorInteractionErrors(user);
  }

  return errors;
}

/**
 * Validates the question author object and returns messages with the errors if it's not valid
 */
export function collectQuestionAuthorInteractionErrors({
  id,
  displayName,
  avatarUrl,
}: IQnAQuestionAuthor): TErrorMessages {
  const errors: TErrorMessages = [];

  /**
   * ID is required for user to match it.
   *  - Stringified number for vimeo users
   *  - UUID for anonymous users
   */
  validateString({ field: id, fieldName: 'id', errors });

  /**
   * For them vimeo profile page is indicating 'No name' label.
   */
  validateString({ field: displayName, fieldName: 'displayName', errors });

  /**
   * Validate avatar url if it exists, all falsy values will be replaced with default placeholder.
   */
  validateString({ field: avatarUrl, fieldName: 'avatarUrl', errors });

  return errors;
}

/**
 * Validates the message author object and returns messages with the errors if it's not valid
 */
export function collectMessageAuthorInteractionErrors({
  id,
  displayName,
  avatarUrl,
  isCreator,
}: PartialRequired<IChatMessageAuthorRecord, 'id' | 'displayName'>): TErrorMessages {
  const errors: TErrorMessages = [];

  /**
   * ID is required for user to match it.
   *  - Stringified number for vimeo users
   *  - UUID for anonymous users
   */
  validateString({ field: id, fieldName: 'id', errors });

  /**
   * In some cases we can have users without name.
   * For them vimeo profile page is indicating 'No name' label.
   */
  if (displayName) {
    validateString({ field: displayName, fieldName: 'displayName', errors });
  }

  /**
   * Check capability flags only if provided.
   */
  if (isCreator !== undefined) {
    validateBoolean({ field: isCreator, fieldName: 'isCreator', errors });
  }

  /**
   * Validate avatar url if it exists, all falsy values will be replaced with default placeholder.
   */
  if (avatarUrl) {
    validateString({ field: avatarUrl, fieldName: 'avatarUrl', errors });
  }

  return errors;
}

/**
 * Validates the message representation object and returns messages with the errors if it's not valid
 */
export function collectMessageInteractionErrors({
  contents,
  createdAt,
  user,
}: PartialRequired<IChatMessageRecord, 'contents' | 'user'>): TErrorMessages {
  const errors: TErrorMessages = [];

  validateString({ field: contents, fieldName: 'contents', errors });

  if (isString(contents)) {
    const validatedMessage: string = contents.trim();

    if (
      validatedMessage.length > interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH ||
      !validatedMessage.length
    ) {
      errors.push(
        `Chat message did not pass validation: ${validatedMessage}, length: ${validatedMessage}`,
      );
    }
  }

  if (createdAt != null) {
    validateDateField({ field: createdAt, fieldName: 'createdAt', errors });
  }

  validateObject({ field: user, fieldName: 'user', errors });

  if (!isUndefined(user) && collectMessageAuthorInteractionErrors(user).length > 0) {
    errors.push(`user should be a plain object, but found  ${JSON.stringify(user)}`);
  }

  return errors;
}
